import React, {useCallback, useEffect, useRef, useState} from "react";
import {Box, Stack, Typography, IconButton, Button, Modal, Fade, LinearProgress, CircularProgress} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon, CheckIcon, File01Icon, Upload01Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {KnowledgeManagerRepository} from "../../../../repositories/KnowledgeManagerRepository";
import {useStompWebsocketKbManager} from "../../../../hooks/useStompWebsocketKbManager";

const ACCEPTED_EXTENSIONS = [".pdf", ".txt", ".md", ".csv", ".json", ".docx", ".xlsx"];
const ACCEPTED_TYPES = [
  "application/pdf", "text/plain", "text/markdown", "text/csv", "application/json",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const formatSize = (bytes) => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const wsStatusToFileStatus = (status) => {
  if (status === "indexed") return "done";
  if (status === "failed") return "error";
  return "processing";
};

export const DocumentUploadModalNew = ({topicId, open, onClose, onUploaded}) => {
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  // For a large/slow upload, the backend can start broadcasting status over the socket before the
  // multipart POST itself has even resolved - i.e. before we know the uploaded doc's id to match
  // against. Buffer any WS update that arrives for a not-yet-known id and replay it once we learn it.
  const unmatchedStatusRef = useRef(new Map());

  // Only connect while a file is actually in flight - nothing to listen for before an upload starts,
  // and the socket drops again once every file has reached a terminal status (indexed/failed)
  const hasInFlight = pendingFiles.some(f => f.status === "uploading" || f.status === "processing");
  const [wsUpdate] = useStompWebsocketKbManager(open && topicId && hasInFlight ? `/topic/topics/${topicId}/documents` : null);

  useEffect(() => {
    if (!wsUpdate?.document) return;
    const {id: docId, status} = wsUpdate.document;
    const fileStatus = wsStatusToFileStatus(status);
    setPendingFiles(prev => {
      if (!prev.some(f => f.uploadedDocId === docId)) {
        unmatchedStatusRef.current.set(docId, fileStatus);
        return prev;
      }
      return prev.map(f => f.uploadedDocId === docId ? {...f, status: fileStatus} : f);
    });
  }, [wsUpdate]);

  const addFiles = useCallback((files) => {
    const newFiles = Array.from(files)
      .filter(f => {
        const ext = "." + f.name.split(".").pop()?.toLowerCase();
        return ACCEPTED_EXTENSIONS.includes(ext) || ACCEPTED_TYPES.includes(f.type);
      })
      .map(f => ({
        file: f, id: `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`, progress: 0, status: "pending",
      }));
    setPendingFiles(prev => [...prev, ...newFiles]);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const removePending = (id) => setPendingFiles(prev => prev.filter(f => f.id !== id));

  const handleUpload = async () => {
    setIsUploading(true);
    const pending = pendingFiles.filter(f => f.status === "pending");
    let succeeded = 0;

    for (const pf of pending) {
      try {
        setPendingFiles(prev => prev.map(f => f.id === pf.id ? {...f, status: "uploading"} : f));
        const uploadedDoc = await KnowledgeManagerRepository.uploadDocument(topicId, pf.file, (pct) => {
          setPendingFiles(prev => prev.map(f => f.id === pf.id ? {...f, progress: pct} : f));
        });
        // A WS update for this doc id may have already arrived (and been buffered) while the
        // upload was still in flight - prefer it over the POST response's own status if so, since
        // it's more recent. Otherwise the WS push takes it from here (processing -> indexed/failed).
        const bufferedStatus = unmatchedStatusRef.current.get(uploadedDoc.id);
        unmatchedStatusRef.current.delete(uploadedDoc.id);
        setPendingFiles(prev => prev.map(f => f.id === pf.id
          ? {...f, progress: 100, status: bufferedStatus || wsStatusToFileStatus(uploadedDoc.status), uploadedDocId: uploadedDoc.id}
          : f));
        succeeded++;
      } catch (err) {
        console.error("[DocumentUploadModalNew] upload failed:", err);
        setPendingFiles(prev => prev.map(f => f.id === pf.id ? {...f, status: "error"} : f));
        toast.error(err.response?.data?.message || `Failed to upload ${pf.file.name}`);
      }
    }

    if (succeeded > 0) {
      toast.success(succeeded === 1 ? "Document uploaded" : `${succeeded} documents uploaded`);
    }

    setIsUploading(false);
    onUploaded?.();
  };

  const handleClose = () => {
    if (isUploading) return;
    setPendingFiles([]);
    onClose();
  };

  const pendingCount = pendingFiles.filter(f => f.status === "pending").length;
  // "Submitted" once every queued file has left the upload step - they may still be processing in the background,
  // tracked live via the WS push, but there's nothing left to do here besides close the modal
  const allSubmitted = pendingFiles.length > 0 && pendingFiles.every(f => !["pending", "uploading"].includes(f.status));

  return (
    <Modal open={open} onClose={handleClose}>
      <Fade in={open}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: {xs: "calc(100% - 24px)", md: "min(520px, 92vw)"}, bgcolor: '#fff', borderRadius: '20px',
          boxShadow: '0 24px 70px rgba(35, 38, 77, 0.22)', p: {xs: 2.5, md: 3}, outline: 'none',
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 2.5}}>
            <Box>
              <Typography sx={{fontSize: 18, fontWeight: 700, color: '#23264D', letterSpacing: '-0.01em'}}>Upload Documents</Typography>
              <Typography sx={{fontSize: 13, color: '#8A8AA0', mt: 0.5}}>PDF, TXT, MD, CSV, JSON, DOCX or XLSX</Typography>
            </Box>
            <IconButton size="small" onClick={handleClose} sx={{color: '#8A8AA0'}}>
              <HugeiconsIcon icon={Cancel01Icon} size={18}/>
            </IconButton>
          </Stack>

          <Box
            onDragOver={(e) => {e.preventDefault(); setIsDragging(true);}}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: "2px dashed", borderColor: isDragging ? "#8491F7" : "#E4E4EE",
              bgcolor: isDragging ? "rgba(132, 145, 247, 0.06)" : "#FAFAFD",
              borderRadius: "14px", p: 4, textAlign: "center", cursor: "pointer",
              transition: "border-color 0.15s, background-color 0.15s",
            }}
          >
            <HugeiconsIcon icon={Upload01Icon} size={28} color={"#8491F7"} style={{marginBottom: 8}}/>
            <Typography sx={{fontSize: 13, color: "#23264D"}}>
              Drop files here or <Box component={"span"} sx={{fontWeight: 700, color: "#4B55C9"}}>browse</Box>
            </Typography>
            <input
              ref={fileInputRef} type={"file"} multiple accept={ACCEPTED_EXTENSIONS.join(",")} style={{display: "none"}}
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </Box>

          {pendingFiles.length > 0 && (
            <Stack gap={1} sx={{mt: 2, maxHeight: 240, overflow: "auto"}}>
              {pendingFiles.map(pf => (
                <Stack key={pf.id} direction="row" alignItems="center" gap={1.5} sx={{p: 1.25, borderRadius: "10px", bgcolor: "#FAFAFD"}}>
                  <HugeiconsIcon icon={File01Icon} size={16} color={"#8A8AA0"}/>
                  <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography sx={{fontSize: 12, color: "#23264D"}} noWrap>{pf.file.name}</Typography>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography sx={{fontSize: 10, color: "#8A8AA0"}}>{formatSize(pf.file.size)}</Typography>
                      {pf.status === "uploading" && (
                        <LinearProgress variant="determinate" value={pf.progress} sx={{
                          flex: 1, height: 4, borderRadius: 2, bgcolor: "#ECECF3",
                          "& .MuiLinearProgress-bar": {bgcolor: "#8491F7"},
                        }}/>
                      )}
                      {pf.status === "processing" && (
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <CircularProgress size={10} sx={{color: "#D9A13A"}}/>
                          <Typography sx={{fontSize: 10, color: "#D9A13A"}}>Processing...</Typography>
                        </Stack>
                      )}
                      {pf.status === "done" && (
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <HugeiconsIcon icon={CheckIcon} size={12} color={"#2F9A6A"}/>
                          <Typography sx={{fontSize: 10, color: "#2F9A6A"}}>Indexed</Typography>
                        </Stack>
                      )}
                      {pf.status === "error" && <Typography sx={{fontSize: 10, color: "#DC2626"}}>Failed</Typography>}
                    </Stack>
                  </Box>
                  {pf.status === "pending" && (
                    <IconButton size="small" onClick={(e) => {e.stopPropagation(); removePending(pf.id);}}>
                      <HugeiconsIcon icon={Cancel01Icon} size={14}/>
                    </IconButton>
                  )}
                </Stack>
              ))}
            </Stack>
          )}

          <Stack direction="row" gap={1.5} justifyContent="flex-end" sx={{mt: 2.5}}>
            <Button
              onClick={handleClose} disabled={isUploading}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px',
                color: '#374151', bgcolor: '#FAFAFD', '&:hover': {bgcolor: '#ECECF3'},
              }}
            >
              {allSubmitted ? "Close" : "Cancel"}
            </Button>
            {!allSubmitted && (
              <Button
                disabled={pendingCount === 0 || isUploading}
                onClick={handleUpload}
                startIcon={isUploading ? <CircularProgress size={14} sx={{color: "#fff"}}/> : <HugeiconsIcon icon={Upload01Icon} size={16}/>}
                sx={{
                  textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px',
                  bgcolor: '#374151', color: '#fff', '&:hover': {bgcolor: '#1F2937'},
                  '&.Mui-disabled': {bgcolor: '#D1D5DB', color: '#9CA3AF'},
                }}
              >
                {isUploading ? "Uploading..." : `Upload ${pendingCount} file(s)`}
              </Button>
            )}
          </Stack>
        </Box>
      </Fade>
    </Modal>
  );
};

export default DocumentUploadModalNew;
